import classnames from 'classnames';
import { noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useAppSelector } from '../../../hooks/react-redux';
import { formatDate, FULL_DATE_FORMAT } from '../../../utils/luxon';
import { getRiskLevelIndicator, getSmileyIcon, riskAssociatedColors } from '../../admin/risk-tolerance/utils';
import { getAllBasicUsers } from '../../admin/users/store';
import { User } from '../../auth/login/store/types';
import { Icon } from '../../shared/icon/Icon';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { WYSIWYG } from '../../shared/wysiwyg/WYSIWYG';
import styles from './DatasetInstance.module.scss';
import { RiskAssociated } from '../../admin/risk-tolerance/store';
import { ClientRiskConversation } from './store/types';

const { primary } = styles;

interface DocumentRiskOverrideConversationProps {
    conversationHeight: number;
    clientRiskConversation: ClientRiskConversation[];
    user: User;
    testId: string;
    isReadOnly: boolean;
}

export const DocumentRiskOverrideConversation: React.FC<DocumentRiskOverrideConversationProps> = ({ conversationHeight, clientRiskConversation, user, testId, isReadOnly }) => {
    const allUsers = useAppSelector(getAllBasicUsers);
    const getIsResponse = useCallback((index: number) => clientRiskConversation.map(({ overwrittenBy }) => overwrittenBy)[index] !== user.userId, [clientRiskConversation, user]);
    const getUsersName = useCallback((overwrittenBy: number) => {
        if (overwrittenBy === 0) {
            return 'System';
        }
        const foundUser = allUsers.find(({ userId }) => userId === overwrittenBy);
        if (foundUser) {
            const { forenames, surname, username } = foundUser;
            return forenames ? `${forenames} ${surname}` : username;
        }
        return 'Unknown User';
    }, [allUsers]);

    const getSmileyDetails = useCallback((overrideRiskLevel: number) => {
        const indicator = getRiskLevelIndicator(overrideRiskLevel);
        const icon = getSmileyIcon(indicator);
        return { fill: riskAssociatedColors[overrideRiskLevel as RiskAssociated], color: primary, icon };
    }, []);

    const emptyMessage = useMemo(() => isReadOnly ? 'Permitted user\'s can override the risk tolerance score assigned to this field. If you would like to have this value changed then please contact your administrator' : 'The risk score associated with this field can be manually overwritten by selecting a different risk score below', [isReadOnly]);

    useEffect(() => {
        const highestIndex = clientRiskConversation.length - 1;
        const lastMessage = document.getElementById(`client-risk-message-${highestIndex}`);
        if (lastMessage) {
            lastMessage.scrollIntoView({ behavior: 'smooth' });
        }
    }, [clientRiskConversation.length]);

    if (clientRiskConversation.length === 0) {
        return (
            <div className={styles.emptyNotes} style={{ height: `${conversationHeight}px` }}>{emptyMessage}</div>
        );
    }

    return (
        <Scrollable height={`${conversationHeight}px`}>
            <div className={styles.documentRiskOverrideConversationWrapper} style={{ height: `${conversationHeight}px` }}>
                {clientRiskConversation.map(({ justification, overrideRiskLevel, overwrittenDate, overwrittenBy, documentExecutedDate }, index) => {
                    const formattedDate = formatDate(overwrittenDate, FULL_DATE_FORMAT);
                    const formattedExecutedDate = formatDate(documentExecutedDate);
                    const { icon, fill, color } = getSmileyDetails(overrideRiskLevel!);
                    const isResponse = getIsResponse(index);
                    const name = getUsersName(overwrittenBy!);
                    return (
                        <div className={styles.messageAndConversationWrapper} key={index} id={`client-risk-message-${index}`}>
                            <div className={classnames(styles.conversationMetaData, { [styles.responseMeta]: isResponse })}>
                                <div className={styles.displayName} data-testid={`${testId}-conversation-${index}-name`}>{name}</div>
                                <div
                                    className={styles.conversationDate}
                                    data-testid={`${testId}-conversation-${index}-date`}
                                >
                                    {formattedDate} ({formattedExecutedDate})
                                </div>
                            </div>
                            <div className={classnames(styles.conversationWrapper, { [styles.userMessage]: !isResponse })}>
                                <div
                                    className={classnames(styles.justificationWrapper, { [styles.messageResponseWrapper]: isResponse })}
                                    data-testid={`${testId}-conversation-${index}-message`}
                                >
                                    <div className={styles.messageIconWrapper}>
                                        <Icon icon={icon} color={color} fill={fill} fontSize={46} />
                                    </div>
                                    <div className={styles.justificationContentWrapper}>
                                        <WYSIWYG
                                            content={justification}
                                            updateContent={noop}
                                            showBorder={false}
                                            height='fit-content'
                                            maxHeight='fit-content'
                                            minHeight='fit-content'
                                            padding='0px'
                                            editorPadding='0px'
                                            showWrapperBorder={false}
                                            toolbarHidden
                                            disabled
                                            isReadOnly
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Scrollable>
    );
};
